import React from 'react'
import { AgTechFormReadOnlyTextControl } from '@agtech/web/components/Forms/Controls/AgTechFormReadOnlyTextControl';
import { AgTechFormRow } from '@agtech/web/components/Forms/Layout/AgTechFormRow';
import AgTechTitleDetailHeader from '@agtech/web/components/Headers/AgTechTitleDetailHeader';
import AgTechFastTabList, { AgTechFastTab } from '@agtech/web/components/Lists/AgTechFastTabList';
import AgTechListDetailPage from '@agtech/web/components/Pages/List/AgTechListDetailPage';
import { IndexLength } from '@agtech/core/utilities/AgTechUtilities';
import { AgTechFormSection } from '@agtech/web/components/Forms/Layout/AgTechFormSection';
import { AgTechContact, ContactDetails } from '@agtech/data/contacts/ContactData';
import { ContactDataProvider, ContactDetailsDataProvider } from '@agtech/data/contacts/ContactDataQueries';
import ContactDialog from '../dialogs/ContactDialog';
import ContactNoteDialog from '../dialogs/notes/ContactNoteDialog';
import ContactNotesCardCollection from '../components/cards/ContactNotesCardCollection';

export type ContactMasterDetailPageProps = {
    contactDetailPage?: (contact: AgTechContact) => JSX.Element,
    contactActionMenu?: (contact: AgTechContact) => JSX.Element
}

const ContactMasterDetailPage = (props: ContactMasterDetailPageProps) => {
    return (
        <ContactDataProvider>
            {(contactData) => {
                const contacts = Object.values(contactData);

                return (
                    <div id='contacts-list-panel' className='stretched non-scrollable flex'>
                        <AgTechListDetailPage
                            items={contacts}
                            itemSurface={cnt => props.contactDetailPage?.(cnt) ?? <ContactMasterDetailPageSurface contact={cnt} />}
                            itemConfig={{
                                height: 80,
                                width: 280,
                                content: cnt => (
                                    <AgTechTitleDetailHeader
                                        title={cnt.firstName + ' ' + cnt.lastName}
                                        detail={cnt.ranchName}
                                        detailMargin={10}
                                        // content={() => props.contactActionMenu?.(cnt) ?? <AgTechContactActionButton contactId={cnt.id} />}
                                        titleFontSize='!text-base'
                                        detailFontSize='text-sm'
                                    />
                                ),
                            }}
                            searchConfig={{
                                placeholder: 'Search contacts...',
                                options: [{ field: 'Name', value: cnt => (cnt.firstName + ' ' + cnt.lastName).toLowerCase() },
                                {field: 'Ranch Name', value: cnt => cnt.ranchName.toLowerCase()},
                                {field: 'Phone Number', value: cnt => cnt.phoneNumber.toLowerCase()}]
                            }}
                            noRecordsConfig={{
                                message: "We're sorry, there are no contacts in the system for this operation",
                                action: {
                                    prompt: 'To create a contact, please click the button below',
                                    button: () => <ContactDialog.Button />
                                }
                            }}
                        />
                    </div>
                )
            }}
        </ContactDataProvider>
    )
}

const ContactMasterDetailPageSurface = (props: { contact: AgTechContact }) => {
    return (
        <ContactDetailsDataProvider contactId={props.contact.id}>
            {(contactDetails) => (
                <div className='row-based stretched non-scrollable fade-in-400 container'>
                    <AgTechFastTabList
                        tabs={[
                            { title: 'General Information', content: () => <ContactGeneralInformationTab contact={contactDetails} />},
                            ContactNotesFastTab(contactDetails)
                        ]}
                    />
                </div>
            )}
        </ContactDetailsDataProvider>
    )
}

export const ContactGeneralInformationTab = (props: { contact: ContactDetails }) => {
    return (
        <div className='row-based stretched container'>
            <AgTechFormRow>
                <AgTechFormReadOnlyTextControl
                    entityType={props.contact}
                    entity={props.contact}
                    caption='First Name'
                    value={cnt => cnt.contact.firstName}
                />
                <AgTechFormReadOnlyTextControl
                    entityType={props.contact}
                    entity={props.contact}
                    caption='Last Name'
                    value={cnt => cnt.contact.lastName}
                />
                <AgTechFormReadOnlyTextControl
                    entityType={props.contact}
                    entity={props.contact}
                    caption='Ranch Name'
                    value={cnt => cnt.contact.ranchName}
                />
            </AgTechFormRow>
            <AgTechFormSection header='Contact Information'>
                <AgTechFormRow>
                    <AgTechFormReadOnlyTextControl
                        entityType={props.contact}
                        entity={props.contact}
                        caption='Email Address'
                        value={cnt => cnt.contact.emailAddress}
                    />
                    <AgTechFormReadOnlyTextControl
                        entityType={props.contact}
                        entity={props.contact}
                        caption='Phone Number'
                        value={cnt => cnt.contact.phoneNumber}
                    />
                </AgTechFormRow>
            </AgTechFormSection>
            <AgTechFormSection header='Address Information'>
            <AgTechFormRow>
                <AgTechFormReadOnlyTextControl
                    entityType={props.contact}
                    entity={props.contact}
                    caption='Address'
                    value={cnt => cnt.contact.address}
                />
                <AgTechFormReadOnlyTextControl
                    entityType={props.contact}
                    entity={props.contact}
                    caption='City'
                    value={cnt => cnt.contact.city}
                />
                <AgTechFormReadOnlyTextControl
                    entityType={props.contact}
                    entity={props.contact}
                    caption='Zip Code'
                    value={cnt => cnt.contact.zip}
                />
                <AgTechFormReadOnlyTextControl
                    entityType={props.contact}
                    entity={props.contact}
                    caption='State'
                    value={cnt => cnt.contact.stateAbbreviation}
                />
                </AgTechFormRow>
            </AgTechFormSection>
        </div>
    )
}

export const ContactNotesFastTab = (contactDetails: ContactDetails): AgTechFastTab => {
    return {
        title: 'Notes',
        config: {
            tabHeight: 200
        },
        content: () => (
            <div className='row-based stretched py-2 container'>
                <ContactNotesCardCollection contactDetails={contactDetails} />
            </div>
        ),
        actionPane: () => IndexLength(contactDetails.notes) > 0 ? (
            <div className='agtech-action-pane'>
                <ContactNoteDialog.Button
                    contactId={contactDetails.contact.id}
                    classes='link thin font-size-standard'
                />
            </div>
        ) : null
    }
}

export default ContactMasterDetailPage